import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Save, X, FileText, Loader2, 
  CheckCircle, AlertCircle, Sparkles 
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TemplateManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    category: 'services',
    content: '',
    html_template: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/marketing-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch templates');
      
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('html_template', formData.html_template);

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/marketing-templates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });

      if (!response.ok) throw new Error('Failed to create template');
      
      toast.success('Template created successfully!');
      setShowCreateForm(false);
      setFormData({ name: '', subject: '', category: 'services', content: '', html_template: '' });
      fetchTemplates();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (templateId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('html_template', formData.html_template);

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/marketing-templates/${templateId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });

      if (!response.ok) throw new Error('Failed to update template');
      
      toast.success('Template updated successfully!');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update template');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/marketing-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete template');
      
      toast.success('Template deleted successfully!');
      fetchTemplates();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (template) => {
    setEditingTemplate(template.id);
    setFormData({
      name: template.name,
      subject: template.subject,
      category: template.category,
      content: template.content || '',
      html_template: template.html_template || ''
    });
  };

  const cancelEdit = () => {
    setEditingTemplate(null);
    setShowCreateForm(false);
    setFormData({ name: '', subject: '', category: 'services', content: '', html_template: '' });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'services': 'bg-blue-100 text-blue-800',
      'offers': 'bg-pink-100 text-pink-800',
      'custom': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.custom;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/customer-admin/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-purple-600" />
                Marketing Template Manager
              </h1>
              <p className="text-gray-600 mt-1">Create and edit marketing email templates</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateForm(true)} 
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Template
          </Button>
        </div>

        {/* Create/Edit Form */}
        {(showCreateForm || editingTemplate) && (
          <Card className="mb-6 shadow-xl border-2 border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <CardTitle>{editingTemplate ? 'Edit Template' : 'Create New Template'}</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={editingTemplate ? (e) => { e.preventDefault(); handleUpdate(editingTemplate); } : handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Template Name</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Summer Sale Campaign"
                      required
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="services">Services</SelectItem>
                        <SelectItem value="offers">Offers</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Email Subject</Label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="e.g., 🎉 Special Offer Just for You!"
                    required
                  />
                </div>
                
                <div>
                  <Label>Content Description</Label>
                  <Textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Brief description of the email content..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label>HTML Template (Optional)</Label>
                  <Textarea
                    name="html_template"
                    value={formData.html_template}
                    onChange={handleInputChange}
                    placeholder="Paste HTML code here for custom email design..."
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <p className="text-sm text-gray-500 mt-1">Leave empty to use default template</p>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={cancelEdit}>
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-700">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {editingTemplate ? 'Update' : 'Create'} Template
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Templates List */}
        {loading && !templates.length ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" />
            <p className="text-gray-600 mt-2">Loading templates...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map(template => (
              <Card key={template.id} className="shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={`text-xs ${getCategoryColor(template.category)}`}>
                      {template.category}
                    </Badge>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(template)}
                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(template.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <CardTitle className="text-lg">{template.name}</CardTitle>
                  <CardDescription className="text-sm">{template.subject}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    {template.content || 'No description'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <FileText className="w-3 h-3" />
                    <span>{template.has_custom_html ? 'Custom HTML' : 'Default Template'}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && templates.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Templates Yet</h3>
              <p className="text-gray-600 mb-4">Create your first marketing email template to get started</p>
              <Button onClick={() => setShowCreateForm(true)} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TemplateManager;
